import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PiX } from 'react-icons/pi';
import Button from './Button';

/**
 * Modal — тёмный оверлей с анимацией.
 * Закрывается по Escape и клику вне контента.
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {string} title
 * @param {React.ReactNode} children
 * @param {React.ReactNode} footer — кнопки подтверждения/отмены
 * @param {'sm'|'md'|'lg'} size
 */
const MODAL_WIDTHS = { sm: '400px', md: '520px', lg: '720px' };

export default function Modal({ isOpen, onClose, title, children, footer, size = 'md' }) {
  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modal = (
    <div className="bm-modal-overlay bm-animate-fade" onClick={onClose}>
      <div
        className="bm-modal bm-animate-in"
        style={{ maxWidth: MODAL_WIDTHS[size] }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Шапка */}
        <div className="bm-modal__header">
          {title && <h3 className="bm-modal__title">{title}</h3>}
          <button className="bm-modal__close" onClick={onClose} aria-label="Закрыть">
            <PiX size={20} />
          </button>
        </div>

        {/* Контент */}
        <div className="bm-modal__body">{children}</div>

        {/* Футер */}
        {footer && <div className="bm-modal__footer">{footer}</div>}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/** Готовый модал подтверждения */
export function ConfirmModal({ isOpen, onClose, onConfirm, title, message, loading, confirmLabel = 'Удалить', confirmVariant = 'danger' }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || 'Подтверждение'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p style={{ color: 'var(--bm-text-secondary)' }}>
        {message || 'Вы уверены? Это действие нельзя отменить.'}
      </p>
    </Modal>
  );
}
